import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type RowData,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { Button } from './button';
import { Spinner } from './spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
import { useViewportWidth } from '../hooks/useBreakpoint';
import {
  countHiddenColumns,
  resolveColumnVisibility,
  type ColumnPriority,
} from '../lib/column-priority';
import { cn } from '../lib/utils';

/**
 * Column priority replaces the hand-written `columnVisibilityModel` each grid
 * used to compute. Declaring it on the column keeps the rule next to the thing
 * it describes, so every grid sheds detail the same way instead of each page
 * inventing its own breakpoints. The rule itself lives in lib/column-priority.
 */
export type { ColumnPriority };

declare module '@tanstack/react-table' {
  // The generics are required by the interface being augmented.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Defaults to 'always'. */
    priority?: ColumnPriority;
    /** Fixed px width. Omit to let the column take its share of the space. */
    width?: number;
    /** Right-aligns and applies tabular figures. For times, speeds, counts. */
    numeric?: boolean;
    /** Excluded from the "hidden at this width" count, e.g. icon columns. */
    incidental?: boolean;
  }
}

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  getRowId: (row: TData) => string;

  loading?: boolean;
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;

  pageSizeOptions?: number[];

  /**
   * Supply `rowCount` to switch the table into server mode: paging and sorting
   * become the caller's responsibility and the table stops slicing rows itself.
   * `pagination`/`sorting` must then be controlled.
   */
  rowCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: (next: PaginationState) => void;
  sorting?: SortingState;
  onSortingChange?: (next: SortingState) => void;

  /** Applied to the scroll container; use it to set the height. */
  className?: string;
}

const DEFAULT_PAGE_SIZES = [10, 25, 50];

export function DataTable<TData>({
  columns,
  data,
  getRowId,
  loading = false,
  onRowClick,
  emptyMessage = 'No rows',
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  rowCount,
  pagination: controlledPagination,
  onPaginationChange,
  sorting: controlledSorting,
  onSortingChange,
  className,
}: DataTableProps<TData>) {
  const serverMode = rowCount !== undefined;

  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSizeOptions[0] ?? 25,
  });
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);

  const pagination = controlledPagination ?? internalPagination;
  const sorting = controlledSorting ?? internalSorting;

  const width = useViewportWidth();

  /**
   * Visibility is derived, never stored — so it re-evaluates on resize rather
   * than sticking at whatever width the page happened to load at.
   */
  const columnVisibility = React.useMemo<VisibilityState>(
    () => resolveColumnVisibility(columns, width),
    [columns, width]
  );

  /** For the footer caption. */
  const hiddenCount = React.useMemo(
    () => countHiddenColumns(columns, width),
    [columns, width]
  );

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => getRowId(row),
    state: { pagination, sorting, columnVisibility },

    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      (onPaginationChange ?? setInternalPagination)(next);
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      (onSortingChange ?? setInternalSorting)(next);
    },

    manualPagination: serverMode,
    manualSorting: serverMode,
    rowCount: serverMode ? rowCount : undefined,

    getCoreRowModel: getCoreRowModel(),
    ...(serverMode
      ? {}
      : { getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel() }),
  });

  const total = serverMode ? (rowCount ?? 0) : data.length;
  const firstRow = total === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const lastRow = Math.min((pagination.pageIndex + 1) * pagination.pageSize, total);
  const visibleColumnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-md border border-line bg-surface">
      {/* The scroll container. Wide tables scroll here rather than pushing the
          page sideways -- the parent must also be `min-w-0` for this to hold
          inside a flex layout. */}
      <div className={cn('relative min-h-0 flex-1 overflow-auto', className)}>
        {loading && (
          <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-brand/15">
            <div className="h-full w-1/3 animate-[overlay-in_0.9s_ease-in-out_infinite_alternate] bg-brand" />
          </div>
        )}

        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const meta = header.column.columnDef.meta;
                  return (
                    <TableHead
                      key={header.id}
                      style={meta?.width ? { minWidth: meta.width } : undefined}
                      className={cn(meta?.numeric && 'text-right')}
                      aria-sort={
                        sorted === 'asc'
                          ? 'ascending'
                          : sorted === 'desc'
                            ? 'descending'
                            : canSort
                              ? 'none'
                              : undefined
                      }
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-sm outline-none',
                            'hover:text-ink focus-visible:ring-2 focus-visible:ring-brand',
                            meta?.numeric && 'flex-row-reverse'
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === 'asc' ? (
                            <ArrowUp aria-hidden className="size-3.5 text-brand" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown aria-hidden className="size-3.5 text-brand" />
                          ) : (
                            <ChevronsUpDown aria-hidden className="size-3.5 text-ink-faint" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  className="py-16 text-center text-ink-faint"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <Spinner className="size-4" label={null} /> Loading…
                    </span>
                  ) : (
                    emptyMessage
                  )}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  interactive={!!onRowClick}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          // Rows are reachable by keyboard, which MUI's grid did
                          // not offer for row clicks.
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(meta?.numeric && 'text-right tabular-nums')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-line px-3 py-2">
        <div className="flex items-center gap-2 text-sm text-ink-soft">
          <span className="tabular-nums">
            {firstRow}–{lastRow} of {total.toLocaleString()}
          </span>
          {hiddenCount > 0 && (
            <span className="hidden sm:inline text-ink-faint">
              · {hiddenCount} column{hiddenCount === 1 ? '' : 's'} hidden at this width
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-ink-soft">
            <span className="hidden sm:inline">Rows</span>
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(value) =>
                (onPaginationChange ?? setInternalPagination)({
                  pageIndex: 0,
                  pageSize: Number(value),
                })
              }
            >
              <SelectTrigger className="h-8 w-[4.5rem] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Previous page"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Next page"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
